import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, X, Hammer, ArrowRight, Sparkles, Lock } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Avatar } from "@/components/common";
import { CategorySelect } from "@/components/CategorySelect";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const input = "w-full bg-transparent border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-cobalt transition-colors";

function CreateModal({ options, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [tags, setTags] = useState([]);
  const [seats, setSeats] = useState(6);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (title.trim().length < 4) return toast.error("Title must be at least 4 characters");
    setBusy(true);
    try {
      const res = await api.post("/build/projects", { title, goal, tags, seats: Number(seats) });
      toast.success("Project created · +30 pts");
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-project-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold">Start a project</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What are we building?"
            className={`${input} mt-2`} data-testid="project-title-input" />
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Goal</label>
          <textarea value={goal} onChange={(e) => setGoal(e.target.value)} rows={3}
            placeholder="Describe the idea and what you want to achieve together."
            className={`${input} mt-2 resize-none`} data-testid="project-goal-input" />
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Tags</label>
          <div className="mt-2"><CategorySelect options={options} value={tags} onChange={setTags} /></div>
        </div>
        <div>
          <label className="eyebrow text-muted-foreground">Seats (max collaborators)</label>
          <input type="number" min={2} max={50} value={seats} onChange={(e) => setSeats(e.target.value)}
            className={`${input} mt-2 w-32`} data-testid="project-seats-input" />
        </div>
        <button onClick={create} disabled={busy} data-testid="create-project-submit"
          className="w-full bg-cobalt text-white rounded-md py-3 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Create project"}
        </button>
      </div>
    </div>
  );
}

function ProjectCard({ p }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/build/${p.project_id}`)} data-testid={`project-card-${p.project_id}`}
      className="group text-left border border-border bg-card rounded-xl p-5 hover:border-cobalt transition-colors flex flex-col">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {p.tags.slice(0, 3).map((t) => (
          <span key={t} className="text-[10px] text-cobalt bg-secondary rounded-full px-2 py-0.5">{t}</span>
        ))}
      </div>
      <h3 className="font-serif text-xl font-bold leading-tight group-hover:text-cobalt transition-colors">{p.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{p.goal}</p>
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Avatar src={p.creator.picture} name={p.creator.name} size={22} />
          <span className="text-xs text-muted-foreground">{p.creator.name}</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${p.is_full ? "text-amber" : "text-emerald"}`}>
          <Users size={13} /> {p.member_count}/{p.seats}{p.is_full ? " full" : ""}
        </span>
      </div>
      <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-cobalt">
        {p.is_member ? "Open workspace" : p.is_full ? "View project" : "Join & build"}
        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
}

export default function Build() {
  const [projects, setProjects] = useState([]);
  const [options, setOptions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.plan === "pro";

  const load = async () => {
    setLoading(true);
    const res = await api.get("/build/projects");
    setProjects(res.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/categories").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Build together" title="Build"
        subtitle="Start or join a project to brainstorm, sketch architectures, map flows and ship code — together."
        action={
          isPro ? (
            <button onClick={() => setShowCreate(true)} data-testid="start-project-button"
              className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors shrink-0">
              <Plus size={16} /> Start a project
            </button>
          ) : (
            <button onClick={() => navigate("/upgrade")} data-testid="build-upgrade-button"
              className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-gold-hover transition-colors shrink-0">
              <Sparkles size={16} /> Go Pro to build
            </button>
          )
        } />

      {!isPro && (
        <div className="border border-cobalt/40 bg-secondary/40 rounded-xl p-5 flex items-start gap-3" data-testid="build-pro-banner">
          <Lock size={18} className="text-cobalt mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold">The Build studio is an Ikigora Pro perk</div>
            <p className="text-xs text-muted-foreground mt-1">You can browse projects below. Go Pro to start or join one and collaborate live.</p>
          </div>
          <button onClick={() => navigate("/upgrade")} className="text-sm font-semibold text-cobalt hover:underline shrink-0">Upgrade →</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl">
          <Hammer size={28} className="mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">No projects yet.{isPro ? " Start the first one." : ""}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => <ProjectCard key={p.project_id} p={p} />)}
        </div>
      )}

      {showCreate && (
        <CreateModal options={options} onClose={() => setShowCreate(false)}
          onCreated={(p) => navigate(`/build/${p.project_id}`)} />
      )}
    </div>
  );
}
